import * as THREE from 'three'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js'
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js'
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js'
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js'
import { FXAAShader } from 'three/examples/jsm/shaders/FXAAShader.js'

import Experience from './Experience'

interface FullscreenShader {
  name: string
  uniforms: Record<string, THREE.IUniform>
  vertexShader: string
  fragmentShader: string
}

const PresentationShader = {
  name: 'PresentationShader',
  uniforms: {
    tDiffuse: { value: null },
    resolution: { value: new THREE.Vector2(1, 1) },
    sharpness: { value: 0.18 },
    exposure: { value: 1.14 },
    lift: { value: 0.035 },
    contrast: { value: 1.01 },
    saturation: { value: 1.03 },
    vignette: { value: 0.025 },
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;

    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform vec2 resolution;
    uniform float sharpness;
    uniform float exposure;
    uniform float lift;
    uniform float contrast;
    uniform float saturation;
    uniform float vignette;

    varying vec2 vUv;

    void main() {
      vec2 texel = 1.0 / resolution;
      vec4 base = texture2D(tDiffuse, vUv);

      vec3 blur =
        texture2D(tDiffuse, vUv + vec2(texel.x, 0.0)).rgb +
        texture2D(tDiffuse, vUv - vec2(texel.x, 0.0)).rgb +
        texture2D(tDiffuse, vUv + vec2(0.0, texel.y)).rgb +
        texture2D(tDiffuse, vUv - vec2(0.0, texel.y)).rgb;
      blur *= 0.25;

      vec3 color = base.rgb + (base.rgb - blur) * sharpness;
      color = color * exposure + lift;
      color = (color - 0.5) * contrast + 0.5;

      float luma = dot(color, vec3(0.2126, 0.7152, 0.0722));
      color = mix(vec3(luma), color, saturation);

      float d = distance(vUv, vec2(0.5));
      float edge = smoothstep(0.82, 0.24, d);
      color *= mix(1.0, edge, vignette);

      gl_FragColor = vec4(clamp(color, 0.0, 1.0), base.a);
    }
  `,
} satisfies FullscreenShader

/** Wraps the WebGLRenderer with production-friendly defaults. */
export default class Renderer {
  private readonly experience = Experience.instance
  public readonly instance: THREE.WebGLRenderer
  private readonly composer: EffectComposer
  private readonly renderPass: RenderPass
  private readonly bloomPass: UnrealBloomPass
  private readonly fxaaPass: ShaderPass
  private readonly presentationPass: ShaderPass

  /** Optional overlay (e.g. the weapon view model) drawn on top of the world. */
  public overlay?: { scene: THREE.Scene; camera: THREE.Camera }

  constructor() {
    const { canvas } = this.experience

    this.instance = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      powerPreference: 'high-performance',
    })

    this.instance.outputColorSpace = THREE.SRGBColorSpace
    // GoldSrc lighting is fully baked into lightmaps — keep colours linear-faithful.
    this.instance.toneMapping = THREE.NoToneMapping
    this.instance.setClearColor('#97a6b4') // sky tone behind skipped sky brushes
    this.instance.shadowMap.enabled = false
    // We clear manually: the overlay (weapon) pass must NOT auto-clear the colour
    // buffer, or it would erase the world we just drew.
    this.instance.autoClear = false

    this.renderPass = new RenderPass(
      this.experience.scene,
      this.experience.camera.instance,
    )
    this.bloomPass = new UnrealBloomPass(
      new THREE.Vector2(1, 1),
      0.09,
      0.55,
      0.88,
    )
    this.presentationPass = new ShaderPass(PresentationShader)
    this.fxaaPass = new ShaderPass(FXAAShader)

    this.composer = new EffectComposer(this.instance)
    this.composer.addPass(this.renderPass)
    this.composer.addPass(this.bloomPass)
    this.composer.addPass(this.presentationPass)
    this.composer.addPass(this.fxaaPass)
    this.composer.addPass(new OutputPass())

    this.resize()
  }

  public resize(): void {
    const { sizes } = this.experience
    this.instance.setSize(sizes.width, sizes.height)
    this.instance.setPixelRatio(sizes.pixelRatio)
    this.composer.setPixelRatio(sizes.pixelRatio)
    this.composer.setSize(sizes.width, sizes.height)

    const renderWidth = Math.max(1, sizes.width * sizes.pixelRatio)
    const renderHeight = Math.max(1, sizes.height * sizes.pixelRatio)
    this.presentationPass.uniforms.resolution.value.set(
      renderWidth,
      renderHeight,
    )
    this.fxaaPass.material.uniforms.resolution.value.set(
      1 / renderWidth,
      1 / renderHeight,
    )
  }

  public renderWithCamera(camera: THREE.Camera, includeOverlay = true): void {
    this.renderPass.camera = camera
    this.instance.clear() // clear colour + depth once per frame
    this.composer.render()

    if (includeOverlay && this.overlay) {
      this.instance.clearDepth() // depth only, so the world's colours survive
      this.instance.render(this.overlay.scene, this.overlay.camera)
    }
  }

  public update(): void {
    this.renderWithCamera(this.experience.camera.instance)
  }

  public dispose(): void {
    this.composer.dispose()
    this.instance.dispose()
  }
}
